import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Polygon, Popup, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Route as RouteIcon, MapPin, AlertTriangle, Hand, Trash2, Loader2, Maximize2 } from "lucide-react";

const numberIcon = (n) => L.divIcon({
  className: "",
  html: `<div style="background:#84CC16;border:2px solid #0F172A;border-radius:50% 50% 50% 0;width:26px;height:26px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.3)"><span style="transform:rotate(45deg);font-size:12px;font-weight:700;color:#0F172A">${n}</span></div>`,
  iconSize: [26, 26], iconAnchor: [13, 26], popupAnchor: [0, -24],
});
const dangerIcon = L.divIcon({ className: "", html: `<div class="danger-pin"></div>`, iconSize: [20, 20], iconAnchor: [10, 10], popupAnchor: [0, -10] });

// polygon area in hectares (spherical)
export function polygonHectares(points) {
  if (!points || points.length < 3) return 0;
  const R = 6378137, rad = (d) => (d * Math.PI) / 180;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i], p2 = points[(i + 1) % points.length];
    area += rad(p2.lon - p1.lon) * (2 + Math.sin(rad(p1.lat)) + Math.sin(rad(p2.lat)));
  }
  return Math.abs((area * R * R) / 2) / 10000;
}

function ClickHandler({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng); } });
  return null;
}
function FitAll({ points, trigger, fallback }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length >= 2) {
      map.fitBounds(L.latLngBounds(points), { padding: [35, 35], maxZoom: 15 });
    } else if (points && points.length === 1) {
      map.setView(points[0], 14);
    } else if (fallback) {
      map.setView(fallback, 12);
    }
  }, [trigger]); // eslint-disable-line
  return null;
}

const MODES = [
  { key: "waypoint", label: "Waypoints", icon: MapPin },
  { key: "zone", label: "Draw zone", icon: RouteIcon },
  { key: "danger", label: "Hazard", icon: AlertTriangle },
  { key: "view", label: "Move / delete", icon: Hand },
];

export default function TaskMap({ type, center, waypoints = [], zone = [], dangers = [], onChange }) {
  const [mode, setMode] = useState("waypoint");
  const [route, setRoute] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [routing, setRouting] = useState(false);
  const [fitKey, setFitKey] = useState(0);

  const set = (patch) => onChange({ waypoints, zone, dangers, ...patch });

  const addPoint = (latlng) => {
    const p = { lat: latlng.lat, lon: latlng.lng, label: "" };
    if (mode === "waypoint") set({ waypoints: [...waypoints, p] });
    else if (mode === "zone") set({ zone: [...zone, p] });
    else if (mode === "danger") set({ dangers: [...dangers, p] });
  };
  const removeFrom = (arr, key, i) => set({ [key]: arr.filter((_, idx) => idx !== i) });
  const moveIn = (arr, key, i, latlng) => set({ [key]: arr.map((x, idx) => idx === i ? { ...x, lat: latlng.lat, lon: latlng.lng } : x) });

  // OSRM driving route for driving tasks
  useEffect(() => {
    if (type !== "driving" || waypoints.length < 2) { setRoute(null); setRouteInfo(null); return; }
    const coords = waypoints.map((p) => `${p.lon},${p.lat}`).join(";");
    setRouting(true);
    fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`)
      .then((r) => r.json())
      .then((d) => {
        const rt = d.routes?.[0];
        if (rt) {
          setRoute(rt.geometry.coordinates.map(([lon, lat]) => [lat, lon]));
          setRouteInfo({ km: (rt.distance / 1000).toFixed(1), min: Math.round(rt.duration / 60) });
        } else { setRoute(null); setRouteInfo(null); }
      })
      .catch(() => { setRoute(null); setRouteInfo(null); })
      .finally(() => setRouting(false));
  }, [type, JSON.stringify(waypoints)]); // eslint-disable-line

  const wpLine = waypoints.map((p) => [p.lat, p.lon]);
  const zonePoly = zone.map((p) => [p.lat, p.lon]);
  const hectares = polygonHectares(zone);
  const initial = center || (waypoints[0] && [waypoints[0].lat, waypoints[0].lon]) || [-26.19, 152.66];

  return (
    <div data-testid="task-map">
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {MODES.map((m) => {
          const active = mode === m.key;
          return (
            <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`task-mode-${m.key}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              <m.icon className="h-3.5 w-3.5" /> {m.label}
            </button>
          );
        })}
        {zone.length > 0 && <button type="button" onClick={() => set({ zone: [] })} data-testid="task-clear-zone" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Clear zone</button>}
        <button type="button" onClick={() => setFitKey((k) => k + 1)} data-testid="task-fit-all" className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-lime-100 text-lime-700 hover:bg-lime-200"><Maximize2 className="h-3.5 w-3.5" /> Fit all</button>
      </div>

      <p className="text-[11px] text-slate-500 mb-2">
        {mode === "waypoint" && "Click the map to drop ordered waypoints. Drag a pin to move it, click it to remove."}
        {mode === "zone" && "Click points around the area to enclose a flight / spray zone."}
        {mode === "danger" && "Click to drop a flashing hazard pin."}
        {mode === "view" && "Drag pins to move them; click a pin to remove it."}
      </p>

      <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height: 320 }}>
        <MapContainer center={initial} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitAll points={[...wpLine, ...zonePoly, ...dangers.map((p) => [p.lat, p.lon]), ...(route || [])]} trigger={fitKey} fallback={center || initial} />
          <ClickHandler onClick={addPoint} />

          {zonePoly.length >= 3 && <Polygon positions={zonePoly} pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 0.3, weight: 2 }} />}
          {zonePoly.length > 0 && zonePoly.length < 3 && <Polyline positions={zonePoly} pathOptions={{ color: "#f59e0b", dashArray: "4", weight: 2 }} />}

          {type === "driving" && route ? (
            <Polyline positions={route} pathOptions={{ color: "#3b82f6", weight: 4 }} />
          ) : wpLine.length > 1 ? (
            <Polyline positions={wpLine} pathOptions={{ color: "#84CC16", weight: 3, dashArray: type === "flight" ? "6" : undefined }} />
          ) : null}

          {waypoints.map((p, i) => (
            <Marker key={`wp-${i}`} position={[p.lat, p.lon]} icon={numberIcon(i + 1)} draggable
              eventHandlers={{ dragend: (e) => moveIn(waypoints, "waypoints", i, e.target.getLatLng()) }}>
              <Popup><div className="text-xs">Waypoint {i + 1}<br /><button className="text-red-600 underline mt-1" onClick={() => removeFrom(waypoints, "waypoints", i)}>Remove</button></div></Popup>
            </Marker>
          ))}
          {dangers.map((p, i) => (
            <Marker key={`dg-${i}`} position={[p.lat, p.lon]} icon={dangerIcon} draggable
              eventHandlers={{ dragend: (e) => moveIn(dangers, "dangers", i, e.target.getLatLng()) }}>
              <Popup><div className="text-xs font-semibold text-red-600">Hazard<br /><button className="text-red-600 underline mt-1" onClick={() => removeFrom(dangers, "dangers", i)}>Remove</button></div></Popup>
            </Marker>
          ))}
          {zone.map((p, i) => (
            <Marker key={`zn-${i}`} position={[p.lat, p.lon]} icon={L.divIcon({ className: "", html: `<div style="background:#f59e0b;border:2px solid #fff;border-radius:50%;width:12px;height:12px"></div>`, iconSize: [12, 12], iconAnchor: [6, 6] })} draggable
              eventHandlers={{ dragend: (e) => moveIn(zone, "zone", i, e.target.getLatLng()) }}>
              <Popup><div className="text-xs">Zone point {i + 1}<br /><button className="text-red-600 underline mt-1" onClick={() => removeFrom(zone, "zone", i)}>Remove</button></div></Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-lime-600" /> {waypoints.length} waypoints</span>
        {dangers.length > 0 && <span className="inline-flex items-center gap-1 text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> {dangers.length} hazards</span>}
        {zone.length >= 3 && <span className="inline-flex items-center gap-1 text-amber-600"><RouteIcon className="h-3.5 w-3.5" /> Zone {hectares.toFixed(2)} ha</span>}
        {type === "driving" && routing && <span className="inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> routing…</span>}
        {type === "driving" && routeInfo && <span className="inline-flex items-center gap-1 text-blue-600"><RouteIcon className="h-3.5 w-3.5" /> {routeInfo.km} km · {routeInfo.min} min</span>}
      </div>
    </div>
  );
}
